import React, { useState, useEffect, useMemo } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Package, 
  Calendar, 
  Download, 
  RotateCcw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Modal from '../components/Modal';
import { inventoryService } from '../services/supabaseService';

const COLORS = [
  { bg: 'rgba(16, 185, 129, 0.1)', text: 'var(--accent-color)' },  // emerald
  { bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b' },              // amber
  { bg: 'rgba(236, 72, 153, 0.1)', text: '#ec4899' },              // pink
  { bg: 'rgba(139, 92, 246, 0.1)', text: '#8b5cf6' },              // purple
  { bg: 'rgba(6, 182, 212, 0.1)', text: '#06b6d4' }                // cyan
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0].split(' ')[0] : '';
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const Inventory = () => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters State (Persistent)
  const [searchTerm, setSearchTerm] = useSessionStorage('inventory_searchTerm', '');
  const [filterCategory, setFilterCategory] = useSessionStorage('inventory_filterCategory', 'All');
  const [filterCondition, setFilterCondition] = useSessionStorage('inventory_filterCondition', 'All');
  const [filterYear, setFilterYear] = useSessionStorage('inventory_filterYear', 'All');
  const [filterMonth, setFilterMonth] = useSessionStorage('inventory_filterMonth', 'All');
  const [startDate, setStartDate] = useSessionStorage('inventory_startDate', '');
  const [endDate, setEndDate] = useSessionStorage('inventory_endDate', '');

  // Pagination State
  const [currentPage, setCurrentPage] = useSessionStorage('inventory_currentPage', 1);
  const [itemsPerPage, setItemsPerPage] = useSessionStorage('inventory_itemsPerPage', 15);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useSessionStorage('inventory_isModalOpen', false);
  const [editingItem, setEditingItem] = useSessionStorage('inventory_editingItem', null);

  const [formData, setFormData] = useSessionStorage('inventory_formData', {
    itemName: '', category: '', quantity: '', cost: '', condition: 'Good', lastUpdated: ''
  });

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const data = await inventoryService.getAll();
      const normalized = (Array.isArray(data) ? data : []).map(item => ({
        ...item,
        itemName: item.itemName || item.item_name || '',
        lastUpdated: item.lastUpdated || (item.last_updated ? item.last_updated.split('T')[0] : '')
      }));
      setInventory(normalized);
    } catch (err) {
      console.error('Error fetching inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  // Available unique categories
  const availableCategories = useMemo(() => {
    const cats = new Set();
    inventory.forEach(item => {
      const c = (item.category || '').trim();
      if (c) cats.add(c);
    });
    return Array.from(cats).sort((a, b) => a.localeCompare(b));
  }, [inventory]);

  // Available unique years from inventory dates
  const availableYears = useMemo(() => {
    const years = new Set();
    inventory.forEach(item => {
      if (item.lastUpdated) {
        const d = parseDateSafe(item.lastUpdated);
        if (d && !isNaN(d.getFullYear())) {
          years.add(d.getFullYear());
        }
      }
    });
    // Add current year if not present
    years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [inventory]);

  // Filter Logic
  const filteredInventory = useMemo(() => {
    return inventory.filter(item => {
      // 1. Search filter
      const matchesSearch = !searchTerm.trim() ||
        (item.itemName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.category || '').toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;

      // 2. Category filter
      if (filterCategory !== 'All') {
        if ((item.category || '').trim().toLowerCase() !== filterCategory.trim().toLowerCase()) {
          return false;
        }
      }

      // 3. Condition filter
      if (filterCondition !== 'All') {
        if ((item.condition || '').toLowerCase() !== filterCondition.toLowerCase()) {
          return false;
        }
      }

      // 4. Date Filtering
      const itemDate = parseDateSafe(item.lastUpdated);

      // 4a. Start Date & End Date custom range
      if (startDate) {
        const start = parseDateSafe(startDate);
        if (itemDate && itemDate < start) return false;
      }
      if (endDate) {
        const end = parseDateSafe(endDate);
        if (itemDate && itemDate > end) return false;
      }

      // 4b. Year filter
      if (filterYear !== 'All') {
        const yr = parseInt(filterYear, 10);
        if (!itemDate || itemDate.getFullYear() !== yr) return false;
      }

      // 4c. Month filter
      if (filterMonth !== 'All') {
        const m = parseInt(filterMonth, 10);
        if (!itemDate || (itemDate.getMonth() + 1) !== m) return false;
      }

      return true;
    });
  }, [inventory, searchTerm, filterCategory, filterCondition, filterYear, filterMonth, startDate, endDate]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCategory, filterCondition, filterYear, filterMonth, startDate, endDate]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredInventory.length / itemsPerPage) || 1;
  const paginatedInventory = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredInventory.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredInventory, currentPage, itemsPerPage]);

  // Calculate totals by category for overview cards (based on filtered list)
  const categoryTotals = useMemo(() => {
    const totals = {};
    filteredInventory.forEach(item => {
      let rawCat = (item.category || '').trim();
      const category = rawCat ? rawCat.charAt(0).toUpperCase() + rawCat.slice(1) : 'Uncategorized';
      const totalValue = (Number(item.quantity) || 0) * (Number(item.cost) || 0);
      totals[category] = (totals[category] || 0) + totalValue;
    });
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [filteredInventory]);

  const grandTotal = useMemo(() => {
    return filteredInventory.reduce((sum, item) => {
      return sum + ((Number(item.quantity) || 0) * (Number(item.cost) || 0));
    }, 0);
  }, [filteredInventory]);

  const totalQuantity = useMemo(() => {
    return filteredInventory.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }, [filteredInventory]);

  // Active filters check
  const isAnyFilterActive = Boolean(
    searchTerm || 
    filterCategory !== 'All' || 
    filterCondition !== 'All' || 
    filterYear !== 'All' || 
    filterMonth !== 'All' || 
    startDate || 
    endDate
  );

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterCategory('All');
    setFilterCondition('All');
    setFilterYear('All');
    setFilterMonth('All');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        itemName: item.itemName || item.item_name || '',
        category: item.category || '',
        quantity: item.quantity || '',
        cost: item.cost || '',
        condition: item.condition || 'Good',
        lastUpdated: item.lastUpdated || (item.last_updated ? item.last_updated.split('T')[0] : '')
      });
    } else {
      setEditingItem(null);
      setFormData({ itemName: '', category: '', quantity: '', cost: '', condition: 'Good', lastUpdated: new Date().toISOString().split('T')[0] });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      item_name: formData.itemName,
      category: formData.category,
      quantity: Number(formData.quantity) || 0,
      cost: Number(formData.cost) || 0,
      condition: formData.condition,
      last_updated: formData.lastUpdated || new Date().toISOString().split('T')[0]
    };

    try {
      if (editingItem) {
        await inventoryService.update(editingItem.id, payload);
      } else {
        await inventoryService.create(payload);
      }
      setIsModalOpen(false);
      fetchInventory();
    } catch (err) {
      console.error('Error saving inventory item:', err);
      alert('Error saving inventory item: ' + err.message);
    }
  };

  const [deleteConfirmItemId, setDeleteConfirmItemId] = useState(null);

  const handleDelete = (id) => {
    setDeleteConfirmItemId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmItemId) return;
    try {
      await inventoryService.delete(deleteConfirmItemId);
      setDeleteConfirmItemId(null);
      fetchInventory();
    } catch (err) {
      console.error('Error deleting inventory item:', err);
      alert('Error deleting inventory item: ' + err.message);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const rows = [
      ['PG Inventory Report'],
      ['Generated On', new Date().toLocaleDateString()],
      ['Filtered Items Count', filteredInventory.length],
      ['Total Asset Value', `Rs ${grandTotal}`],
      [],
      ['Item Name', 'Category', 'Quantity', 'Unit Cost (Rs)', 'Total Value (Rs)', 'Condition', 'Last Updated']
    ];

    filteredInventory.forEach(item => {
      const totalVal = (Number(item.quantity) || 0) * (Number(item.cost) || 0);
      rows.push([
        `"${(item.itemName || '').replace(/"/g, '""')}"`,
        `"${(item.category || '').replace(/"/g, '""')}"`,
        item.quantity || 0,
        item.cost || 0,
        totalVal,
        item.condition || 'Good',
        item.lastUpdated || ''
      ]);
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Inventory_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: 'var(--primary-color)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }} />
        <p>Loading inventory assets...</p>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '3rem' }}>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '1.6rem', fontWeight: '700', margin: 0 }}>Inventory Management</h2>
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              padding: '0.3rem 0.75rem',
              borderRadius: '9999px',
              backgroundColor: 'rgba(59, 130, 246, 0.15)',
              color: 'var(--primary-color)',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              {filteredInventory.length} Items Listed
            </span>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.4rem' }}>
            Track and manage all PG assets, furniture, appliances, and maintenance history with date filters.
          </p>
        </div>

        <div className="flex items-center flex-wrap" style={{ gap: '0.75rem' }}>
          <button 
            className="btn-primary flex items-center gap-2" 
            onClick={handleExportCSV}
            style={{ padding: '0.45rem 0.9rem', fontSize: '0.875rem', backgroundColor: 'rgba(255,255,255,0.08)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
          >
            <Download size={15} /> Export CSV
          </button>
          <button className="btn-primary flex items-center gap-2" onClick={() => handleOpenModal()} style={{ padding: '0.45rem 0.9rem', fontSize: '0.875rem' }}>
            <Plus size={16} /> Add Item
          </button>
        </div>
      </div>

      {/* FILTER CONTROLS CARD */}
      <div className="card mb-6" style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          
          {/* Top Row: Search & Dropdown Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            
            {/* Search Input */}
            <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '200px' }}>
              <Search 
                size={16} 
                style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} 
              />
              <input
                type="text"
                placeholder="Search items or categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem 0.5rem 2.2rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem'
                }}
              />
            </div>

            {/* Category Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem'
                }}
              >
                <option value="All" style={{ color: '#000' }}>All Categories</option>
                {availableCategories.map(cat => (
                  <option key={cat} value={cat} style={{ color: '#000' }}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Condition Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <select
                value={filterCondition}
                onChange={e => setFilterCondition(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem'
                }}
              >
                <option value="All" style={{ color: '#000' }}>All Conditions</option>
                <option value="Good" style={{ color: '#000' }}>Good</option>
                <option value="Fair" style={{ color: '#000' }}>Fair</option>
                <option value="Poor" style={{ color: '#000' }}>Poor</option>
              </select>
            </div>

            {/* Year Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <select
                value={filterYear}
                onChange={e => setFilterYear(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem'
                }}
              >
                <option value="All" style={{ color: '#000' }}>All Years</option>
                {availableYears.map(yr => (
                  <option key={yr} value={yr} style={{ color: '#000' }}>{yr}</option>
                ))}
              </select>
            </div>

            {/* Month Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <select
                value={filterMonth}
                onChange={e => setFilterMonth(e.target.value)}
                style={{
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem'
                }}
              >
                <option value="All" style={{ color: '#000' }}>All Months</option>
                {MONTH_NAMES.map((name, i) => (
                  <option key={name} value={i + 1} style={{ color: '#000' }}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Bottom Row: Date Range (From / To) & Reset Filter Button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid rgba(255,255,255,0.06)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Calendar size={14} color="var(--primary-color)" /> Date Range:
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{
                    padding: '0.35rem 0.6rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    color: 'var(--text-primary)',
                    fontSize: '0.8rem'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{
                    padding: '0.35rem 0.6rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    color: 'var(--text-primary)',
                    fontSize: '0.8rem'
                  }}
                />
              </div>
            </div>

            {/* Clear Filters Button */}
            {isAnyFilterActive && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-1"
                style={{
                  padding: '0.35rem 0.75rem',
                  borderRadius: '0.375rem',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: 'var(--danger-color)',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                <RotateCcw size={12} /> Clear All Filters
              </button>
            )}
          </div>

        </div>
      </div>

      {/* Category & Asset Overview Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', 
        gap: '1.25rem', 
        marginBottom: '2rem' 
      }}>
        {/* Grand Total Card */}
        <div className="card flex items-center gap-5" style={{ padding: '1.25rem', borderLeft: '4px solid var(--primary-color)' }}>
          <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary-color)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Package size={22} />
          </div>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem' }}>
              {isAnyFilterActive ? 'Filtered Asset Value' : 'Total Asset Value'}
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>₹{grandTotal.toLocaleString()}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
              {totalQuantity} total units across {filteredInventory.length} items
            </div>
          </div>
        </div>

        {/* Dynamic Category Cards */}
        {categoryTotals.slice(0, 5).map(([category, amount], index) => {
          const color = COLORS[index % COLORS.length];
          return (
            <div key={category} className="card flex items-center gap-5" style={{ padding: '1.25rem', borderLeft: `4px solid ${color.text}` }}>
              <div style={{ padding: '0.75rem', backgroundColor: color.bg, color: color.text, borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Package size={22} />
              </div>
              <div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500', marginBottom: '0.25rem', textTransform: 'capitalize' }}>
                  {category}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                  ₹{amount.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                  {grandTotal > 0 ? `${((amount / grandTotal) * 100).toFixed(1)}% of total` : '0%'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Inventory Table Card */}
      <div className="card" style={{ padding: '1.25rem' }}>
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Showing <strong>{filteredInventory.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}</strong> to <strong>{Math.min(currentPage * itemsPerPage, filteredInventory.length)}</strong> of <strong>{filteredInventory.length}</strong> items
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={e => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-color)',
                backgroundColor: 'rgba(0,0,0,0.2)',
                color: 'var(--text-primary)',
                fontSize: '0.8rem'
              }}
            >
              <option value={10} style={{ color: '#000' }}>10</option>
              <option value={15} style={{ color: '#000' }}>15</option>
              <option value={25} style={{ color: '#000' }}>25</option>
              <option value={50} style={{ color: '#000' }}>50</option>
              <option value={100} style={{ color: '#000' }}>100</option>
            </select>
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>Item Name</th>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>Category</th>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>Quantity</th>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>Unit Cost (₹)</th>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>Total Value (₹)</th>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>Condition</th>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>Last Updated</th>
                <th style={{ padding: '0.85rem 0.5rem', fontWeight: '600', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInventory.map((item) => {
                const totalItemCost = (Number(item.quantity) || 0) * (Number(item.cost) || 0);
                return (
                  <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.15s ease' }}>
                    <td style={{ padding: '0.85rem 0.5rem', fontWeight: '600', color: 'var(--text-primary)' }}>{item.itemName}</td>
                    <td style={{ padding: '0.85rem 0.5rem', color: 'var(--text-secondary)' }}>
                      <span style={{
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        fontSize: '0.75rem'
                      }}>
                        {item.category || 'General'}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 0.5rem', fontWeight: '600' }}>{item.quantity}</td>
                    <td style={{ padding: '0.85rem 0.5rem', color: 'var(--text-secondary)' }}>₹{Number(item.cost || 0).toLocaleString()}</td>
                    <td style={{ padding: '0.85rem 0.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>₹{totalItemCost.toLocaleString()}</td>
                    <td style={{ padding: '0.85rem 0.5rem' }}>
                      <span style={{
                        padding: '0.2rem 0.6rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: item.condition === 'Good' ? 'rgba(16, 185, 129, 0.1)' : 
                                         item.condition === 'Fair' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: item.condition === 'Good' ? 'var(--accent-color)' : 
                               item.condition === 'Fair' ? '#f59e0b' : 'var(--danger-color)'
                      }}>
                        {item.condition}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 0.5rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{item.lastUpdated || '-'}</td>
                    <td style={{ padding: '0.85rem 0.5rem', textAlign: 'right' }}>
                      <div className="flex justify-end gap-2">
                        <button 
                          onClick={() => handleOpenModal(item)} 
                          style={{ padding: '0.4rem', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', border: 'none' }}
                          title="Edit item"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(item.id)} 
                          style={{ padding: '0.4rem', background: 'transparent', color: 'var(--danger-color)', cursor: 'pointer', border: 'none' }}
                          title="Delete item"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredInventory.length === 0 && (
            <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <Package size={36} style={{ margin: '0 auto 0.75rem', opacity: 0.4 }} />
              <p style={{ fontWeight: '500', fontSize: '0.95rem' }}>No inventory items match the selected filters.</p>
              {isAnyFilterActive && (
                <button
                  onClick={handleClearFilters}
                  style={{
                    marginTop: '0.75rem',
                    padding: '0.4rem 0.9rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'var(--primary-color)',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1.25rem',
            paddingTop: '1rem',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Page {currentPage} of {totalPages}
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.35rem 0.6rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: currentPage === 1 ? 'rgba(255,255,255,0.2)' : 'var(--text-primary)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronLeft size={14} />
              </button>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                // Show first, last, current, and adjacent pages
                if (
                  pageNum === 1 || 
                  pageNum === totalPages || 
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      style={{
                        padding: '0.35rem 0.65rem',
                        borderRadius: '0.375rem',
                        border: pageNum === currentPage ? '1px solid var(--primary-color)' : '1px solid var(--border-color)',
                        backgroundColor: pageNum === currentPage ? 'var(--primary-color)' : 'rgba(0,0,0,0.2)',
                        color: pageNum === currentPage ? '#fff' : 'var(--text-primary)',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: pageNum === currentPage ? '700' : 'normal'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 || 
                  pageNum === currentPage + 2
                ) {
                  return <span key={pageNum} style={{ color: 'var(--text-secondary)', padding: '0 0.2rem' }}>...</span>;
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.35rem 0.6rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  color: currentPage === totalPages ? 'rgba(255,255,255,0.2)' : 'var(--text-primary)',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Inventory Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingItem ? "Edit Item" : "Add Inventory Item"}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Item Name</label>
            <input required type="text" value={formData.itemName} onChange={e => setFormData({ ...formData, itemName: e.target.value })} placeholder="e.g. Office Chair" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Category</label>
              <input required type="text" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} placeholder="e.g. Furniture" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Quantity</label>
              <input required type="number" value={formData.quantity} onChange={e => setFormData({ ...formData, quantity: e.target.value })} placeholder="e.g. 10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Unit Cost (₹)</label>
              <input type="number" value={formData.cost} onChange={e => setFormData({ ...formData, cost: e.target.value })} placeholder="e.g. 1500" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Condition</label>
              <select
                value={formData.condition}
                onChange={e => setFormData({ ...formData, condition: e.target.value })}
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="Good" style={{ color: '#000' }}>Good</option>
                <option value="Fair" style={{ color: '#000' }}>Fair</option>
                <option value="Poor" style={{ color: '#000' }}>Poor</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Last Updated</label>
              <input required type="date" value={formData.lastUpdated} onChange={e => setFormData({ ...formData, lastUpdated: e.target.value })} />
            </div>
          </div>
          <button type="submit" className="btn-primary mt-4">
            {editingItem ? 'Update Item' : 'Save Item'}
          </button>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirmItemId} onClose={() => setDeleteConfirmItemId(null)} title="Confirm Delete">
        <div className="flex flex-col gap-4">
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
            Are you sure you want to delete this inventory item? This action cannot be undone.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
            <button 
              type="button"
              onClick={() => setDeleteConfirmItemId(null)} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                border: '1px solid var(--border-color)', 
                background: 'rgba(255,255,255,0.05)', 
                color: 'var(--text-primary)', 
                cursor: 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '500' 
              }}
            >
              Cancel
            </button>
            <button 
              type="button"
              onClick={confirmDelete} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                border: 'none', 
                backgroundColor: 'var(--danger-color)', 
                color: '#fff', 
                cursor: 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '600' 
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Inventory;

